import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { GENDERS } from 'components/intake/PatientInfoSection';
import { useEnvironment } from 'context/environment';
import { useAllowedFacilities } from 'hooks/useAllowedFacilities';
import { portalConnectionQueryKeys } from 'services/api/portalConnections';
import { portalScriptQueryKeys } from 'services/api/portalScripts';
import { saveWorkflowActionProgress, workflowQueryKeys } from 'services/api/workflows';
import { uploadPortalDocuments } from 'services/portalDocuments';
import {
  finishManualPortal,
  launchManualPortal,
  ManualPortalField,
  onManualPortalResult,
  runPortalRetrieval,
} from 'services/portalExtension';
import { useToastActions } from 'stores/toastStore';

import { ATTACHMENTS_VALUE_NAME, PORTAL_COLLECT_FIELDS, portalCollectionOutputs } from '../constants';

export interface ManualLaunchRequest {
  workflowId: string;
  actionId: string;
  entryUrl: string;
  sourceSystem: string;
  // What the portal calls this patient, passed through so the HUD can help the person find them.
  portalPatientName?: string;
  // Record mode: the extension arms the recorder instead of the manual HUD — the person's
  // portal run records a fresh script, and the captured values stage on the action when
  // they finish. `produces` is the retrieval action's declared outputs, widened to the
  // collect-field set the same way every other retrieval mode is.
  record?: boolean;
  produces?: string[];
}

interface ManualLaunchState {
  // The task whose portal tab is open right now, or null. At most one, browser-wide: the
  // extension refuses a second manual job, so the UI arbitrates rather than letting a person
  // discover the refusal at click time.
  activeWorkflowId: string | null;
  launchingWorkflowId: string | null;
  // The task whose record-mode run is in flight, or null. Distinct from activeWorkflowId:
  // a recording has no launchId to close, and it ends only when the person finishes (or
  // abandons) in the portal tab — the values then stage on the action from here.
  recordingWorkflowId: string | null;
  // Tasks whose `Open in <portal>` has been acked this session. Session-scoped on purpose —
  // persisting it would need new run state, and a reload legitimately means "start over".
  unlockedWorkflowIds: Set<string>;
  launch: (request: ManualLaunchRequest) => Promise<void>;
  close: () => void;
}

const ManualLaunchContext = createContext<ManualLaunchState | null>(null);

/**
 * Owns the `Open in <portal>` launch for the whole page.
 *
 * It lives ABOVE the card list on purpose. `launchId` used to be component state on the one
 * card that offered the button; on a list that inserts and removes cards live, any refetch
 * that remounts a card would tear down the result subscription AND skip `manualClose`, leaving
 * the extension's single job slot occupied so every later launch fails.
 */
export function ManualLaunchProvider({ children }: { children: React.ReactNode }) {
  const { addToast } = useToastActions();
  const { environment } = useEnvironment();
  const queryClient = useQueryClient();
  const { facilities: allowedFacilities } = useAllowedFacilities();
  const [active, setActive] = useState<{ launchId: string; workflowId: string } | null>(null);
  const [launchingWorkflowId, setLaunchingWorkflowId] = useState<string | null>(null);
  const [recordingWorkflowId, setRecordingWorkflowId] = useState<string | null>(null);
  const [unlockedWorkflowIds, setUnlocked] = useState<Set<string>>(new Set());

  // The teardown path reads the live launch without re-running the unmount effect on every
  // launch — a dependency on `active` would fire manualClose the moment a launch resolved.
  const activeRef = useRef(active);
  activeRef.current = active;
  const recordingRef = useRef(recordingWorkflowId);
  recordingRef.current = recordingWorkflowId;

  // Abandoning a task is the normal case on a list (unlike the single action card this moved
  // off, where completion was the only exit), so the tab is closed on teardown too.
  useEffect(
    () => () => {
      if (activeRef.current) finishManualPortal(activeRef.current.launchId);
    },
    []
  );

  useEffect(() => {
    if (!active) return;
    return onManualPortalResult(active.launchId, () => {
      // The HUD's send (or a closed tab's drafts) ends the launch. The captured values are the
      // slideout's business — it owns the action write — and the card's job is done once the
      // task is unlocked, which the ack already did.
      setActive(null);
    });
  }, [active]);

  // Record mode, ported from the retired retrieval card: the recorder runs as long as the
  // person takes in the portal tab, then the captured values (documents uploaded first)
  // stage on the action via save_progress — the refreshed run leads with the review form.
  const recordScript = useCallback(
    async ({ workflowId, actionId, entryUrl, sourceSystem, portalPatientName, produces }: ManualLaunchRequest) => {
      const outputFields = portalCollectionOutputs(produces ?? []);
      const expectedOutputs = outputFields.map((output) => output.name);
      const documentOutputs = expectedOutputs.filter((name) => name === ATTACHMENTS_VALUE_NAME);
      setRecordingWorkflowId(workflowId);
      try {
        const raw = await runPortalRetrieval({
          record: true,
          entryUrl,
          params: { patient_name: portalPatientName ?? '' },
          context: { workflowId, actionId, sourceSystem, retrievalPointer: entryUrl },
          expectedOutputs,
          documentOutputs,
          outputFields,
        });
        const values = await uploadPortalDocuments(raw, environment?.OLIO_FILES_DOMAIN);
        const workflow = await saveWorkflowActionProgress({ workflowId, actionId, outputs: values });
        queryClient.setQueryData(workflowQueryKeys.show(workflowId), workflow);
        queryClient.invalidateQueries({ queryKey: workflowQueryKeys.lists });
        // The run just recorded a script, so the launch pane's script lookup and the
        // Connected Portals readiness rows are both stale.
        queryClient.invalidateQueries({ queryKey: portalScriptQueryKeys.list(sourceSystem) });
        queryClient.invalidateQueries({ queryKey: portalConnectionQueryKeys.list() });
      } catch (error) {
        addToast({ text: error instanceof Error ? error.message : 'Portal recording failed.' });
      } finally {
        setRecordingWorkflowId(null);
      }
    },
    [addToast, environment?.OLIO_FILES_DOMAIN, queryClient]
  );

  const launch = useCallback(
    async (request: ManualLaunchRequest) => {
      const { workflowId, actionId, entryUrl, sourceSystem, portalPatientName } = request;
      if (activeRef.current || recordingRef.current) {
        addToast({ text: 'Close the portal tab you already have open before opening another.' });
        return;
      }
      if (request.record) {
        await recordScript(request);
        return;
      }
      setLaunchingWorkflowId(workflowId);
      try {
        // The same declaration the retrieval form renders, described to the HUD: it has no idea
        // what any field means, so the datatype and the options travel with it.
        const facilityOptions = allowedFacilities.map((group) => ({ value: group.id, label: group.name }));
        const fields: ManualPortalField[] = PORTAL_COLLECT_FIELDS.map(({ name, label, type, cardinality }) => ({
          name,
          label,
          cardinality,
          type: type === 'facility' ? 'select' : type === 'ssn' ? 'text' : type,
          ...(type === 'facility' ? { options: facilityOptions } : {}),
          ...(type === 'select' ? { options: GENDERS } : {}),
        }));
        const { launchId } = await launchManualPortal({
          entryUrl,
          expectedOutputs: fields.map((field) => field.name),
          fields,
          params: { patient_name: portalPatientName ?? '' },
          context: { workflowId, actionId, sourceSystem, retrievalPointer: entryUrl },
        });
        setActive({ launchId, workflowId });
        setUnlocked((current) => new Set(current).add(workflowId));
      } catch (error) {
        addToast({ text: error instanceof Error ? error.message : 'Could not open the portal tab.' });
      } finally {
        setLaunchingWorkflowId(null);
      }
    },
    [addToast, allowedFacilities, recordScript]
  );

  const close = useCallback(() => {
    if (activeRef.current) finishManualPortal(activeRef.current.launchId);
    setActive(null);
  }, []);

  const value = useMemo(
    () => ({
      activeWorkflowId: active?.workflowId ?? null,
      launchingWorkflowId,
      recordingWorkflowId,
      unlockedWorkflowIds,
      launch,
      close,
    }),
    [active, launchingWorkflowId, recordingWorkflowId, unlockedWorkflowIds, launch, close]
  );

  return <ManualLaunchContext.Provider value={value}>{children}</ManualLaunchContext.Provider>;
}

export function useManualLaunch(): ManualLaunchState {
  const context = useContext(ManualLaunchContext);
  if (!context) throw new Error('useManualLaunch must be used inside a ManualLaunchProvider');
  return context;
}
